import React, { useState, useEffect } from 'react';
import { cvData } from '../data/cvData';
import '../styles/CV.css';

// 学术报告 / 海报共用：会议名 + 加粗标题 + 可选奖项 + 时间地点
function TalkList({ title, items }) {
  if (!items?.length) return null;
  return (
    <>
      <h3 className="cv-subsection-title">{title}</h3>
      <ul className="cv-misc-list">
        {items.map((it, i) => (
          <li key={i}>
            {it.venue}. <strong>“{it.title}”</strong>
            {it.award && <> — <em>{it.award}</em></>}
            {' '}({it.location})
          </li>
        ))}
      </ul>
    </>
  );
}

// 作者串里的 ‖ 是共同一作标记，渲染成上标；脚注在列表末尾统一说明
function Authors({ value }) {
  return (
    <>
      {value.split('‖').map((part, i, all) => (
        <React.Fragment key={i}>
          {part}
          {i < all.length - 1 && <sup className="pub-eq">‖</sup>}
        </React.Fragment>
      ))}
    </>
  );
}

function CV() {
  const [activeSection, setActiveSection] = useState('education');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.cv-section');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 200) setActiveSection(section.id);
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(id);
  };

  const navItems = [
    { id: 'education', label: 'Education' },
    { id: 'research', label: 'Research' },
    { id: 'publications', label: 'Publications' },
    { id: 'misc', label: 'Misc. Experience' },
    { id: 'awards', label: 'Awards' },
  ];

  return (
    <div className="page-container cv-page">
      <header className="cv-header">
        <h1 className="cv-name">{cvData.name}</h1>
        {/* Address / email line hidden per request — re-enable to show:
        <p className="cv-contact-line">
          {cvData.address} &nbsp;·&nbsp;
          <a href={`mailto:${cvData.email}`}>{cvData.email}</a>
        </p>
        */}
        {(cvData.researchGate || cvData.googleScholar || cvData.github) && (
          <p className="cv-social-line">
            {cvData.googleScholar && (
              <a href={cvData.googleScholar} target="_blank" rel="noopener noreferrer">Google Scholar</a>
            )}
            {cvData.googleScholar && cvData.researchGate && <span className="cv-social-sep">·</span>}
            {cvData.researchGate && (
              <a href={cvData.researchGate} target="_blank" rel="noopener noreferrer">ResearchGate</a>
            )}
            {(cvData.researchGate || cvData.googleScholar) && cvData.github && <span className="cv-social-sep">·</span>}
            {cvData.github && (
              <a href={cvData.github} target="_blank" rel="noopener noreferrer">GitHub</a>
            )}
          </p>
        )}
      </header>

      <main className="cv-main-layout">
        {/* ── CONTENT ── */}
        <div className="cv-content">

          {/* EDUCATION */}
          <section className="cv-section" id="education">
            <h2 className="cv-section-title">Education</h2>
            {cvData.education.map((item, i) => (
              <div className="cv-item" key={i}>
                <div className="cv-date">{item.date}</div>
                <div className="cv-role">{item.degree}</div>
                <div className="cv-institution">{item.institution}</div>
                <div className="cv-description">{item.detail}</div>
              </div>
            ))}
          </section>

          {/* RESEARCH */}
          <section className="cv-section" id="research">
            <h2 className="cv-section-title">Research</h2>
            {cvData.research.map((item, i) => (
              <div className="cv-item" key={i}>
                <div className="cv-date">{item.date}</div>
                <div className="cv-role">{item.role}</div>
                <div className="cv-institution">{item.institution}</div>
                <ul className="cv-bullets">
                  {item.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              </div>
            ))}
          </section>

          {/* PUBLICATIONS */}
          <section className="cv-section" id="publications">
            <h2 className="cv-section-title">Publications</h2>
            <div className="publication-list">
              {cvData.publications.map((pub, i) => {
                const inner = (
                  <>
                    <div className="pub-meta">{pub.journal}</div>
                    <span className="pub-title">{pub.title}</span>
                    <p className="pub-authors"><Authors value={pub.authors} /></p>
                  </>
                );
                const cls = `publication-item${pub.highlight ? ' highlighted' : ''}${pub.url ? ' pub-linked' : ''}`;
                return pub.url ? (
                  <a
                    className={cls}
                    key={i}
                    href={pub.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="打开文献"
                  >{inner}</a>
                ) : (
                  <article className={cls} key={i}>{inner}</article>
                );
              })}
            </div>
            <p className="pub-footnote"><sup className="pub-eq">‖</sup> Equal contribution</p>
          </section>

          {/* MISC */}
          <section className="cv-section" id="misc">
            <h2 className="cv-section-title">Miscellaneous Experience</h2>

            <TalkList title="Invited Talks" items={cvData.invitedTalks} />
            <TalkList title="Oral Presentations" items={cvData.conferences} />
            <TalkList title="Posters" items={cvData.posters} />

            <h3 className="cv-subsection-title">Teaching</h3>
            <ul className="cv-misc-list">
              {cvData.teaching.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </section>

          {/* AWARDS */}
          <section className="cv-section" id="awards">
            <h2 className="cv-section-title">Scholarships & Awards</h2>
            <ul className="cv-misc-list">
              {cvData.awards.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </section>


        </div>

        {/* ── SIDEBAR (RIGHT) ── */}
        <aside className="cv-sidebar">
          <nav className="cv-nav">
            {navItems.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                className={`cv-nav-link ${activeSection === id ? 'active' : ''}`}
              >
                {label}
              </a>
            ))}
          </nav>
          <button
            className="btn btn-primary cv-download-btn"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/resume.pdf';
              link.download = 'CV_LingeLi.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            ↓ Download PDF
          </button>
        </aside>
      </main>
    </div>
  );
}

export default CV;
